import type { FactEvidence } from '../../../../backend/src/matter/fact/fact-evidence';
import { DocFact, FactDetails } from '@/api/types/backend-dto-types';
import { BoundingBoxWithId, BoundingBox, TimeRange, TimeRangeWithId } from '@/types';

const PAGINATED_SPREADSHEET_EXTENSIONS = new Set(['xlsx', 'xlsm']);

/**
 * Resolve the number of pages the viewer should render.
 *
 * `numberOfPages` stores the sheet count for spreadsheets and must stay that way
 * for billing. xlsx/xlsm workbooks can be re-paginated during conversion, so their
 * rendered PDF may hold more pages than sheets — for those, prefer the page count
 * observed from the document itself (the loaded PDF's metadata, or the rasterised
 * page-dimensions list) and only fall back to the stored sheet count while neither
 * is available yet.
 */
export const resolveViewerPageCount = ({
  extension,
  numberOfPages,
  loadedPdfPageCount,
  pageDimensionsCount,
}: {
  extension?: string | null;
  numberOfPages?: number | null;
  loadedPdfPageCount?: number | null;
  pageDimensionsCount?: number | null;
}): number => {
  const normalisedExtension = extension?.toLowerCase().replace(/^\./, '');
  const isPaginatedSpreadsheet = !!normalisedExtension && PAGINATED_SPREADSHEET_EXTENSIONS.has(normalisedExtension);

  if (isPaginatedSpreadsheet) {
    return loadedPdfPageCount || pageDimensionsCount || numberOfPages || 0;
  }

  return numberOfPages ?? loadedPdfPageCount ?? 0;
};

/**
 * Generic fact type with minimum required fields for highlight processing.
 * Works with both DocFactDto and FactDetailsResponseDto.
 */
interface FactWithLocations {
  id: string;
  locations: Array<FactLocationPosition & { docId: string }>;
}

export interface FactLocationPosition {
  boundingBoxes: BoundingBox[] | null;
  timeRanges: TimeRange[] | null;
}

export const factEvidence = (position: FactLocationPosition): FactEvidence | null => {
  if (position.timeRanges) return { kind: 'timestampRange', ranges: position.timeRanges };
  if (position.boundingBoxes) return { kind: 'boundingBox', boxes: position.boundingBoxes };
  return null;
};

/**
 * The page a fact location starts on, or undefined when it has no page — either
 * because nothing was captured or because the source isn't paginated at all.
 *
 * Undefined rather than 0 or 1 on purpose: `useDocumentNavigation` only sets
 * `docPageNumber` when the value is truthy, so absent means "don't jump anywhere".
 */
export const getFactLocationPageNumber = (position: FactLocationPosition): number | undefined =>
  position.boundingBoxes?.[0]?.pageNumber;

export const processFactHighlights = (facts: FactWithLocations[] | DocFact[] | FactDetails[], docId: string) => {
  const coordinates: BoundingBoxWithId[] = [];
  const timeRanges: TimeRangeWithId[] = [];
  const highlightFactMap = new Map<string, number>();

  facts.forEach((fact, index) => {
    highlightFactMap.set(fact.id, index);
    fact.locations.forEach((location) => {
      // Only process the fact highlights for the current doc we are viewing
      if (location.docId === docId) {
        // A location carries boxes or time ranges, never both.
        coordinates.push(
          ...(location.boundingBoxes ?? []).map((boundingBox) => ({
            ...boundingBox,
            id: fact.id,
          })),
        );
        timeRanges.push(...(location.timeRanges ?? []).map((timeRange) => ({ ...timeRange, id: fact.id })));
      }
    });
  });

  timeRanges.sort((a, b) => a.startSeconds - b.startSeconds);

  // Sort coordinates by page number, then by top position (lowest first)
  coordinates.sort((a, b) => {
    const pageCompare = a.pageNumber - b.pageNumber;
    if (pageCompare !== 0) return pageCompare;
    return a.top - b.top;
  });

  return { coordinates, timeRanges, highlightFactMap };
};

/**
 * Sort key for a fact's position within one document: `[major, minor]`, comparable
 * only against other keys from the same document. Null means "no position", which
 * always sorts last.
 */
const appearanceKey = (fact: FactWithLocations, docId: string): [number, number] | null => {
  const location = fact.locations.find((candidate) => candidate.docId === docId);
  if (!location) return null;

  const evidence = factEvidence(location);
  if (evidence === null) return null;

  switch (evidence.kind) {
    case 'boundingBox': {
      const first = evidence.boxes[0];
      return first === undefined ? null : [first.pageNumber, first.top];
    }
    case 'timestampRange': {
      const first = evidence.ranges[0];
      return first === undefined ? null : [first.startSeconds, 0];
    }
  }
};

/**
 * Orders facts by where they appear in the given document. Returns a new array —
 * callers pass react-query cache arrays straight in, which must not be mutated.
 */
export const sortFactsByAppearance = <T extends FactWithLocations>(facts: T[], docId: string): T[] => {
  // Keys are precomputed so the comparator is a consistent total order. Deriving them
  // inside the comparator previously returned 1/-1 asymmetrically for position-less
  // facts, which V8's sort assumes never happens and which could reorder facts that
  // do have positions.
  const keys = new Map<T, [number, number] | null>(facts.map((fact) => [fact, appearanceKey(fact, docId)]));

  return [...facts].sort((a, b) => {
    const aKey = keys.get(a) ?? null;
    const bKey = keys.get(b) ?? null;

    if (aKey === null || bKey === null) {
      if (aKey === bKey) return 0;
      return aKey === null ? 1 : -1;
    }

    return aKey[0] - bKey[0] || aKey[1] - bKey[1];
  });
};
